import { VideoEventResponse } from '$Generated/api';
import { moment } from 'Imports';
import { IIntegrationPartnerManager } from 'integrationPartner/integrationPartnerAbstraction';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { ConfigService } from '$State/ConfigFreezerService';
import { UserManagementService } from '$State/UserManagementFreezerService';
import { StandardProtectedRoutePaths } from '../router/route-sets/standard-route-config';
import { GeotabRoutePaths } from '../router/route-sets/geotab-route-config';
const GeotabApi: any = require('mg-api-js');

export interface GeotabUserCredentials {
    Name: string;
    SessionId: string;
    Database: string;
    Server: string;
}

export interface GeotabAuthentication {
    credentials: {
        database: string;
        userName: string;
        password: string;
    };
    path?: string;
}

export class GeotabManager implements IIntegrationPartnerManager {
    geotabApiObject: any;
    private isInitialized: boolean = false;
    private loginPromise: Promise<boolean> | null = null;

    public validateGeotabGroupsFilter(): void {
        var regExp = /(?!filter:!\()([a-zA-Z0-9,]*)(?=\))/;
        var geotabGroups = regExp.exec(location.hash);
        IntegrationPartnerDataService.setGroupsFilter(geotabGroups ? geotabGroups[0].toString() : '');
    }

    public configureApplicationForIntegratedMode(onDestroyCallback?: () => void): void {
        (window as any).geotab.addin.video_platform = () => {
            return {
                initialize: (api: any, state: any, callback: any) => {
                    this.saveGeotabApi(api);
                    if (callback) {
                        callback();
                    }
                    window.onpopstate = (event) => {
                        this.validateGeotabGroupsFilter();
                    };
                },
                focus: (api: any, state: any) => {
                    this.saveGeotabApi(api, 'onFocus');
                    this.validateGeotabGroupsFilter();

                    //ensure that css is enabled
                    if (GeotabManager.getCssElements().length === 0) {
                        GeotabManager.restoreCssElements(() => {
                            IntegrationPartnerDataService.setCanRender(true);
                        });
                    } else {
                        //set can render to true if the file already exists to handle case where the data service got out of sync due to multiple instances
                        IntegrationPartnerDataService.setCanRender(true);
                    }
                },
                blur: (api: any, state: any) => {
                    this.clearGeotabApi();

                    if (!location.hash.includes('addin-videoprotects') && !location.hash.includes('addin-vidlocate')) {
                        //disable videoprotect styles when plugin loses focus and the new route is not a videoprotects page
                        IntegrationPartnerDataService.setCanRender(false);
                        GeotabManager.removeCssElements();
                    }

                    if (onDestroyCallback) {
                        onDestroyCallback();
                    }
                },
            };
        };
    }

    public configureApplicationForStandAloneMode(): void {
        // Currently there isn't anything to configure for the stand-alone geotab
    }

    public async handleCredentialExpiration(): Promise<void> {
        // Nothing to do in Geotab to handle credential expiration at the moment
    }

    public async getCredential(): Promise<string> {
        return UserManagementService.getUserCredential();
    }

    public async tryLogin(): Promise<boolean> {
        if (ConfigService.isRunningStandAlone() && !location.pathname.includes('/login')) {
            // If we are running geotab stand alone and not on the login page then we need to create the api object
            return this.trySetGeotabApi();
        }
        return UserManagementService.isLoggedIn();
    }

    public logout(): void {
        this.clearGeotabApi();
        this.redirectToLogin();
    }

    public getMapUrl(data: VideoEventResponse | undefined): any {
        if (!data) {
            return undefined;
        }

        const geotabUserCreds = GeotabManager.getGeotabUserCredentials();

        const sessionId = geotabUserCreds ? geotabUserCreds.SessionId : '';
        const username = geotabUserCreds ? geotabUserCreds.Name : '';
        const database = geotabUserCreds ? geotabUserCreds.Database : '';

        const server = geotabUserCreds?.Server ? `https://${geotabUserCreds.Server}` : location.origin;
        const credentials = '#credentials:(database:' + database + ",sessionId:'" + sessionId + "',userName:'" + username + "')";

        // small window - if trip start date is not within, the trip history / activity will be empty
        const mapEndDate = moment.utc(data.eventEndDate).add(15, 'minutes').toISOString();
        const mapStartDate = moment.utc(data.eventStartDate).subtract(15, 'minutes').toISOString();

        const mapUrl =
            server +
            '/' +
            database +
            '/#tripsHistory,' +
            credentials +
            ",dateRange:(endDate:'" +
            mapEndDate +
            "',startDate:'" +
            mapStartDate +
            "'),devices:!(" +
            data.telematicDeviceSourceId +
            ')';

        return mapUrl;
    }

    public getLinkUrl(standaloneUrl: string): string {
        if (ConfigService.isRunningStandAlone()) {
            return standaloneUrl;
        } else {
            let integratedHash;
            switch (standaloneUrl.toLowerCase()) {
                case StandardProtectedRoutePaths.VideoRecall:
                    integratedHash = `#${GeotabRoutePaths.VidLocateVideoRecall}`;
                    break;
                case StandardProtectedRoutePaths.DeviceAssociation:
                    integratedHash = `#${GeotabRoutePaths.VidLocateDeviceAssociations}`;
                    break;
                case StandardProtectedRoutePaths.Installers:
                    integratedHash = `#${GeotabRoutePaths.VidLocateInstallers}`;
                    break;
                case StandardProtectedRoutePaths.Settings:
                    integratedHash = `#${GeotabRoutePaths.VidLocateSettings}`;
                    break;
                case StandardProtectedRoutePaths.VideoDashboard:
                    integratedHash = `#${GeotabRoutePaths.VidLocateVideoDashboard}`;
                    break;
                case StandardProtectedRoutePaths.Home:
                case StandardProtectedRoutePaths.VideoEvents:
                default:
                    integratedHash = `#${GeotabRoutePaths.VidLocateVideoEvents}`;
                    break;
            }

            let newIntegratedHash = integratedHash;
            if (location.hash.includes('videoprotects')) {
                newIntegratedHash = integratedHash.replace('vidlocate', 'videoprotects');
            }

            return '/' + GeotabManager.getGeotabUserCredentialsDatabase() + '/' + newIntegratedHash;
        }
    }

    public postAuthentication(authentication: GeotabAuthentication, redirect: string) {
        try {
            const geotabApi: any = new GeotabApi(authentication, {
                // Override the default of true, true will continue to use the session, server, db, user stored in local storage and not the supplied credentials
                // In our scenario this results in potentially creating an api object for something other than what the user entered in our login form
                // https://github.com/Geotab/mg-api-js
                rememberMe: true,
            });

            geotabApi
                .getSession()
                .then((result: any) => {
                    if (redirect === window.location.pathname) {
                        redirect = StandardProtectedRoutePaths.VideoEvents;
                    }
                    window.location.href = redirect;
                })
                .catch((error: any) => {
                    console.error('getSession failed:', error);
                    // Handle error
                });
        } catch (error) {
            console.log(error);
        }
    }

    private saveGeotabApi(geotabApi: any, caller: 'onFocus' | 'default' = 'default'): Promise<boolean> {
        this.geotabApiObject = geotabApi;

        return this.getSessionAsync(this.geotabApiObject)
            .then((session: any) => {
                if (caller === 'onFocus' && !this.isInitialized) {
                    return false;
                }
                this.isInitialized = true;

                if (session !== undefined) {
                    const credentials = GeotabManager.isIntegrated() ? session : session.credentials;
                    const geotabUserCredentialString = JSON.stringify({
                        Name: credentials.userName,
                        SessionId: credentials.sessionId,
                        Database: credentials.database,
                        Server: 'my.geotab.com',
                    });

                    UserManagementService.setUserCredential(geotabUserCredentialString);
                    return true;
                } else {
                    console.error('GeotabManager.saveGeotabApi(...):  Failed to acquire session information from the Geotab Api object.');
                    this.redirectToLogin();
                    return false;
                }
            })
            .catch((error: any) => {
                console.error('GeotabManager.saveGeotabApi(...): getSession failed:', error);
                this.redirectToLogin();
                return false;
            });
    }

    private getSessionAsync(api: any): Promise<any> {
        if (!api || typeof api.getSession !== 'function') {
            return Promise.reject(new Error('Geotab API object is missing or does not implement getSession().'));
        }

        // mg-api-js v3: getSession() returns a Promise
        try {
            const result = api.getSession();
            if (result && typeof result.then === 'function') {
                return result;
            }
        } catch (e) {
            return Promise.reject(e);
        }

        // legacy: geotabs integrated api object uses callback pattern
        return new Promise((resolve, reject) => {
            try {
                api.getSession((session: any) => resolve(session));
            } catch (e) {
                reject(e);
            }
        });
    }

    private trySetGeotabApi(): Promise<boolean> {
        // Prevent multiple simultaneous login attempts
        if (this.loginPromise) {
            return this.loginPromise;
        }

        this.loginPromise = new Promise<boolean>((resolve) => {
            // Restore auth from localStorage (we lose the api object on redirect, but sessionId persists)
            try {
                const stored = localStorage.getItem('geotabAPI_credentials');
                if (!stored) {
                    this.redirectToLogin();
                    resolve(false);
                    return;
                }

                // localStorage shape (per prompt): { database, sessionId, userName }
                const parsed = JSON.parse(stored) as { database?: string; userName?: string; sessionId?: string };

                const database = parsed?.database;
                const userName = parsed?.userName;
                const sessionId = parsed?.sessionId;

                // Required when authenticating via sessionId
                const path = 'my.geotab.com';

                if (!database || !userName || !sessionId) {
                    this.redirectToLogin();
                    resolve(false);
                    return;
                }

                const authentication = {
                    credentials: {
                        database,
                        userName,
                        password: '',
                        sessionId,
                    },
                    path,
                };

                const api: any = new GeotabApi(authentication, { rememberMe: true });

                api.getSession()
                    .then(() => this.saveGeotabApi(api))
                    .then((success: boolean) => resolve(success))
                    .catch((error: any) => {
                        console.error('GeotabManager.trySetGeotabApi() getSession failed:', error);
                        this.redirectToLogin();
                        resolve(false);
                    });
            } catch (e) {
                console.error('GeotabManager.trySetGeotabApi() failed:', e);
                this.redirectToLogin();
                resolve(false);
            }
        }).finally(() => {
            this.loginPromise = null;
        });

        return this.loginPromise;
    }

    private clearGeotabApi() {
        if (ConfigService.isRunningStandAlone()) {
            // api.forget() removes the local storage values that the api object uses to set credentials (let geotab manage this in integrated mode)
            if (this.geotabApiObject && typeof this.geotabApiObject.forget === 'function') {
                this.geotabApiObject.forget();
            }
        }

        // Clear our api object from freezer
        this.geotabApiObject = null;
    }

    private redirectToLogin(): void {
        // If we're integrated into the GeoTab portal, we do NOT want to navigate to the login page (only for stand-alone)
        if (!GeotabManager.isIntegrated()) {
            location.href = '/login';
        }
    }

    public static isIntegrated(): boolean {
        return (window as any).geotab !== undefined;
    }

    public static getGeotabUserCredentialsDatabase(): string {
        const creds = GeotabManager.getGeotabUserCredentials();
        return creds ? creds.Database : '';
    }

    private static getGeotabUserCredentials(): GeotabUserCredentials {
        return JSON.parse(UserManagementService.getUserCredential());
    }

    public static getGeotabBaseUrl(baseUrl: string, apiBaseUrl: string): string {
        let url = location.origin;
        // chaeck to see if the base configuration is set already (this works when loaded directly and not from the mygeotab intergration) //
        // and if not, use the ServiceConfig.EntryPoint environment variable, set on the docker container or from VS debugger //
        // by using an intercept controller that replaces this dummy string with the correct value //
        // NOTE: this is truely a string equaliing "undefined" and is not a typo/bug //
        if (baseUrl !== 'undefined' || apiBaseUrl === undefined) {
            // Hacky, i know but we need to get the original domain for images and such, so this is attempting to find it on an injected script.
            //.querySelector will return the first match of the comma delimited list of selectors(we should only have one present)
            const scriptelement = document.querySelector(
                "script[src*='yaharasoftware.com'], script[src*='videoprotects.com'], script[src*='videoprotects.net']",
            );
            const src = scriptelement !== null ? scriptelement.getAttribute('src') : '';
            const urlObject = GeotabManager.parseURL(src);
            url = 'https://' + urlObject.hostname + '/';
        }
        return url;
    }

    private static parseURL(url: string | null | undefined): any {
        const parser = document.createElement('a');
        // Let the browser do the work
        parser.href = url === null || url === undefined ? '' : url;
        return {
            protocol: parser.protocol,
            host: parser.host,
            hostname: parser.hostname,
            port: parser.port,
            pathname: parser.pathname,
            search: parser.search,
            hash: parser.hash,
        };
    }

    private static getCssElements(): NodeListOf<Element> {
        return document.querySelectorAll(`head link[href='${ConfigService.getBaseUrl()}/bundle.css']`);
    }

    private static removeCssElements() {
        GeotabManager.getCssElements().forEach((element) => {
            element.remove();
        });
    }

    private static restoreCssElements(onLoadListener: (this: HTMLLinkElement, ev: Event) => any) {
        var newCssElement = document.createElement('link');
        newCssElement.href = `${ConfigService.getBaseUrl()}/bundle.css`;
        newCssElement.setAttribute('rel', 'stylesheet');
        newCssElement.addEventListener('load', onLoadListener, { once: true });
        document.head.appendChild(newCssElement);
    }
}
